const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Types
export interface Settings {
    id: number;
    gst: number;
    created_at: string;
    updated_at: string;
}

export interface UpdateSettingsData {
    gst: number;
}

export interface Backup {
    filename: string;
    size: string;
    size_bytes: number;
    created_at: string;
}

export interface TariffCard {
    id: number;
    weight: string;
    same_city: number;
    same_zone: number;
    diff_zone: number;
    service_type: string;
    order_type: string;
    created_at: string;
    updated_at: string;
}

export interface TariffCards {
    overnight_cod: TariffCard[];
    overnight_prepaid: TariffCard[];
    express_cod: TariffCard[];
    express_prepaid: TariffCard[];
}

export interface UpdateTariffCardData {
    same_city_500: number;
    same_zone_500: number;
    diff_zone_500: number;
    same_city_1: number;
    same_zone_1: number;
    diff_zone_1: number;
    same_city_2: number;
    same_zone_2: number;
    diff_zone_2: number;
    same_city_3: number;
    same_zone_3: number;
    diff_zone_3: number;
    same_city_extra: number;
    same_zone_extra: number;
    diff_zone_extra: number;
}

export type TariffCardType = 'overnight-cod' | 'overnight-prepaid' | 'express-cod' | 'express-prepaid';

interface BackendResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
};

export const settingsApi = {
    // Get current settings
    getSettings: async (): Promise<Settings | null> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/settings`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ settings: Settings | null }> = await response.json();
        return data.data.settings;
    },

    // Update settings
    updateSettings: async (settingsData: UpdateSettingsData): Promise<Settings> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/settings`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settingsData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ settings: Settings }> = await response.json();
        return data.data.settings;
    },

    // Get all backups
    getBackups: async (): Promise<Backup[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/settings/backups`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ backups: Backup[] }> = await response.json();
        return data.data.backups;
    },

    // Create a new backup
    createBackup: async (): Promise<string | null> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/settings/backups`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ filename: string | null }> = await response.json();
        return data.data.filename;
    },

    // Download a backup file
    downloadBackup: async (filename: string): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/settings/backups/${encodeURIComponent(filename)}/download`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        // Create a blob from the response and trigger download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    },

    // Delete a backup
    deleteBackup: async (filename: string): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/settings/backups/${encodeURIComponent(filename)}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Get all tariff cards
    getTariffCards: async (): Promise<TariffCards> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/tariff-cards`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ tariff_cards: TariffCards }> = await response.json();
        return data.data.tariff_cards;
    },

    // Update a tariff card by type
    updateTariffCard: async (type: TariffCardType, tariffData: UpdateTariffCardData): Promise<TariffCard[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/tariff-cards/${type}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(tariffData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ tariff_cards: TariffCard[] }> = await response.json();
        return data.data.tariff_cards;
    },
};
